import { makeStyles } from "tss-react/mui"
import { Box, Skeleton, Stack } from "@mui/material"
import { tailwindPalette } from "../../../theme/tailwindPalette"
import {
    SectionBlockSkeleton,
    SectionItemRowSkeleton,
    OrderAmountSkeleton,
    SectionItemLabelSkeleton,
    SectionItemDataSkeleton,
    BlockTitleSkeleton,
    SectionSubTitleSkeleton,
    TableColumnLabelSkeleton,
} from "../../../../shared/components/dialogs/receiptConfirmOrderDialog/components/common/ReceiptPrimitives"

const useStyles = makeStyles()((theme) => ({
    slideContainer: {
        flex: 1,
        display: "flex",
        justifyContent: "center",
        marginTop: "1.5rem",
        "@media print": {
            flex: "none",
            display: "block",
            backgroundColor: "transparent",
            padding: 0,
            margin: 0,
            width: "210mm",
            maxWidth: "210mm",
            boxSizing: "border-box",
            pageBreakInside: "auto",
        },
        [theme.breakpoints.down("sm")]: {
            marginTop: "0 !important",
        },
    },
    slide: {
        width: "100%",
        maxWidth: "49.625rem",
        padding: "3.5rem",
        backgroundColor: "white",
        boxShadow: "0px 0px 10px 0px #00000026;",
        "@media print": {
            width: "100%",
            maxWidth: "210mm",
            padding: "1rem",
            boxShadow: "none",
            backgroundColor: "white",
            margin: "0 auto",
            pageBreakInside: "avoid",
        },
        [theme.breakpoints.down("sm")]: {
            boxShadow: "none",
            width: "100%",
            padding: "1rem",
            gap: "2rem",
        },
    },
    detailsContainer: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "1.5rem",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
            gap: "0",
        },
    },
    detailsBox: {
        background: tailwindPalette.blue[50],
        [theme.breakpoints.down("sm")]: {
            background: theme.palette.common.white,
            borderRadius: 0,
            padding: "2rem 1.25rem",
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    billReceiverBox: {
        background: tailwindPalette.emerald[50],
        [theme.breakpoints.down("sm")]: {
            background: "white",
            borderRadius: 0,
            paddingTop: 0,
            paddingBottom: "2rem",
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    billReceiverWrap: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "4rem",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
        },
    },
    orderSummarySection: {
        borderTop: `1px solid ${theme.palette.divider}`,
        paddingTop: "1rem",
    },
    // Order Summary Table styles
    tableHeader: {
        display: "grid",
        gridTemplateColumns: "1fr auto auto",
        gap: "2rem",
        backgroundColor: tailwindPalette.slate[100],
        padding: "0.75rem 1.25rem",
        borderRadius: "4px 4px 0 0",
    },
    tableRow: {
        display: "grid",
        gridTemplateColumns: "1fr auto auto",
        gap: "2rem",
        padding: "0.25rem 1.25rem",
        alignItems: "center",
    },
    tableDeliveryRow: {
        padding: "0.75rem 1.25rem 0",
    },
    tableFooter: {
        display: "flex",
        justifyContent: "flex-end",
        gap: "1.5rem",
        padding: "1rem 1.25rem",
        alignItems: "center",
    },
    footerLabels: {
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        alignItems: "flex-end",
    },
    footerValues: {
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
        alignItems: "flex-end",
    },
}))

function SkeletonDetailsBox({ rows = 5, className }: { rows?: number; className?: string }) {
    return (
        <SectionBlockSkeleton classNames={className}>
            <Stack gap="0.5rem">
                {Array.from({ length: rows }).map((_, index) => (
                    <SectionItemRowSkeleton key={index} />
                ))}
            </Stack>
        </SectionBlockSkeleton>
    )
}

function SkeletonBillReceiverBox({ className }: { className?: string }) {
    const { classes } = useStyles()

    return (
        <SectionBlockSkeleton classNames={className} titleWidth={120}>
            <Box className={classes.billReceiverWrap}>
                {/* Left column: Company info */}
                <Stack gap="0.5rem">
                    <SectionItemLabelSkeleton width={150} />
                    <SectionItemLabelSkeleton width={180} />
                </Stack>
                {/* Right column: Cost centers */}
                <Stack gap="0.5rem">
                    <SectionItemLabelSkeleton width={100} />
                    <SectionItemLabelSkeleton width={120} />
                </Stack>
            </Box>
        </SectionBlockSkeleton>
    )
}

function SkeletonOrderTableRow() {
    const { classes } = useStyles()

    return (
        <Box className={classes.tableRow}>
            <SectionItemDataSkeleton width={160} />
            <SectionItemDataSkeleton width={80} />
            <SectionItemDataSkeleton width={70} />
        </Box>
    )
}

function SkeletonOrderSummaryTable({ rows = 3 }: { rows?: number }) {
    const { classes } = useStyles()

    return (
        <Stack gap="2rem">
            <BlockTitleSkeleton width={140} />
            <Box>
                {/* Table Header */}
                <Box className={classes.tableHeader}>
                    <TableColumnLabelSkeleton width={120} />
                    <TableColumnLabelSkeleton width={50} />
                    <TableColumnLabelSkeleton width={70} />
                </Box>

                {/* Delivery subtitle */}
                <Box className={classes.tableDeliveryRow}>
                    <SectionSubTitleSkeleton width={200} />
                </Box>

                {/* Order lines */}
                {Array.from({ length: rows }).map((_, index) => (
                    <SkeletonOrderTableRow key={index} />
                ))}

                {/* Footer with totals */}
                <Box className={classes.tableFooter}>
                    <Box className={classes.footerLabels}>
                        <SectionItemDataSkeleton width={60} />
                        <SectionItemLabelSkeleton width={80} />
                    </Box>
                    <Box className={classes.footerValues}>
                        <OrderAmountSkeleton width={100} />
                        <SectionItemLabelSkeleton width={70} />
                    </Box>
                </Box>
            </Box>
        </Stack>
    )
}

export function ReceiptDetailsSkeleton() {
    const { classes } = useStyles()

    return (
        <Box id="printable-content" className={classes.slideContainer}>
            <Stack direction="column" spacing="2.5rem" className={classes.slide}>
                {/* Header: Meeting name + ID and Status badge */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                    <Stack direction="row" alignItems="center" gap="1.5rem">
                        <Skeleton variant="text" width={280} height={48} animation="wave" />
                        <SectionItemLabelSkeleton width={60} />
                    </Stack>
                    <Skeleton variant="rounded" width={100} height={32} animation="wave" />
                </Stack>

                {/* Details section: 2-column grid with blue boxes */}
                <Box className={classes.detailsContainer}>
                    <SkeletonDetailsBox rows={5} className={classes.detailsBox} />
                    <SkeletonDetailsBox rows={2} className={classes.detailsBox} />
                </Box>

                {/* Bill Receiver section: green box */}
                <SkeletonBillReceiverBox className={classes.billReceiverBox} />

                {/* Order Summary Table */}
                <SkeletonOrderSummaryTable rows={3} />
            </Stack>
        </Box>
    )
}
